import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ArticleIcon from '@mui/icons-material/Article';
import GavelIcon from '@mui/icons-material/Gavel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

import ConsignorCard from './ConsignmentComponents/ConsignorCard';
import DocumentSlot from './ConsignmentComponents/DocumentSlot';
import CarSourceField from './ConsignmentComponents/CarSourceField';
import SignatureStatusDialog from './SignatureStatusDialog';
import InPersonSignDialog from './InPersonSignDialog';
import ConsignorBosDialog from './ConsignorBosDialog';
import UpdateNTODialog from './UpdateNTODialog';
import SalesCreditDialog from '../SalesCreditDialog';
import {
  CREDIT_SOURCE,
  creditsLabel,
  normalizeCredits,
  resolveNtoEntryCredits,
} from '../../utilities/salesCredits';
import firebase from '../../utilities/firebase';
import store from '../../utilities/store';
import { StateManager } from '../../utilities/stateManager';
import usePermissions from '../../utilities/usePermissions';
import RequestManager from '../../utilities/requestManager';
import { applyTitleOdometerBrand } from '../../utilities/titleOdometer';
import constants from '../../utilities/constants';
import { useDatePickerSlotProps } from '../../utilities/ModalStackContext';
import { isValidCarSource, validateCarSource } from '../../utilities/carSource';

export default function Consignor({ car = {} }) {
  const { isAllowed, isManager, isAdmin, userType } = usePermissions();
  const stockNumber = car.stock;
  const consignor = car.consignor || {};
  const datePickerSlotProps = useDatePickerSlotProps();

  const [printDisabled, setPrint] = React.useState(false);
  StateManager.setPrint = setPrint;

  const [salesTeam, setSalesTeam] = React.useState([]);
  const [sigOpen, setSigOpen] = React.useState(false);
  const [bosOpen, setBosOpen] = React.useState(false);
  const [bosStatusOpen, setBosStatusOpen] = React.useState(false);
  const [inPersonOpen, setInPersonOpen] = React.useState(false);
  const [inPersonUrl, setInPersonUrl] = React.useState('');
  const [inPersonSigner, setInPersonSigner] = React.useState('');
  const [ntoDialogOpen, setNtoDialogOpen] = React.useState(false);
  const [ntoHistoryOpen, setNtoHistoryOpen] = React.useState(false);
  // { index, entry } — which history entry's sales credit is being edited.
  const [editingCredit, setEditingCredit] = React.useState(null);
  const [savingCredit, setSavingCredit] = React.useState(false);

  const ntoHistory = Array.isArray(car.nto_history) ? car.nto_history : [];
  // Newest first for display, but each row keeps the index it has in the stored array —
  // that is what a credit edit writes back to.
  const ntoHistoryRows = ntoHistory
    .map((entry, index) => ({ entry, index }))
    .reverse();
  // Only a real reduction — with no updated_nto there is nothing to compare against.
  const ntoReduction = (Number(car.nto) && Number(car.updated_nto))
    ? Math.max(0, Number(car.nto) - Number(car.updated_nto))
    : 0;

  React.useEffect(() => {
    firebase.firestore().collection('users').where('salesRep', '==', true).get().then(snap => {
      const team = snap.docs
        .map(d => ({ id: d.id, name: [d.data().firstName, d.data().lastName].filter(Boolean).join(' ') }))
        .filter(t => t.name)
        .sort((a, b) => a.name.localeCompare(b.name));
      if (team.length) setSalesTeam(team);
    });
  }, []);

  const updater = (id, value) => {
    firebase.firestore().doc('cars/' + stockNumber).set({ [id]: value }, { merge: true });
    StateManager.updateCar({ [id]: value });
  };

  const onLicenseUpdate = (fieldId, value) => {
    StateManager.updateCar({ consignor: { ...consignor, [fieldId]: value } });
  };

  const onCarFieldUpdate = (fieldId, value) => {
    StateManager.updateCar({ [fieldId]: value });
    if (fieldId === 'title_front' && value) applyTitleOdometerBrand(stockNumber, value);
  };

  const getPDF = async () => {
    if (!validateForm(car)) return null;
    return await RequestManager.get({ function: 'preparePDF', variables: { stockNumber, doctype: 'consign' } });
  };

  const viewPDF = async () => {
    StateManager.setLoading(true);
    const pdfDoc = await getPDF();
    if (!pdfDoc?.file) {
      StateManager.setAlertAndOpen('Hmm… there was an error.', 'error');
    } else {
      const a = document.createElement('a');
      a.href = pdfDoc.file;
      a.target = '_blank';
      a.click();
    }
    StateManager.setLoading(false);
  };

  const CONSIGN_MESSAGE = 'Thank you for choosing Skyway Classics to sell your classic car. Please review and sign the following consignment agreement so we can get your car listed as soon as possible!\n\nIf you encounter any issues, please call your consignment representative for assistance.\n\nAgain, thank you for choosing Skyway Classics. We are looking forward to conducting business with you!';

  const buildSigners = () => (consignor && consignor.email
    ? [{ role: 'consignor', email: consignor.email, name: consignor.display_name || [consignor.first_name, consignor.last_name].filter(Boolean).join(' ').trim() || consignor.email }]
    : []);

  const sessionVariables = (inPerson) => ({
    docType: 'consignment',
    refId: stockNumber,
    doctype: 'consign',
    signers: buildSigners(),
    inPerson,
    name: `${car.year || ''} ${car.make || ''} ${car.model || ''} Consignment Packet`,
    subject: `${car.year || ''} ${car.make || ''} ${car.model || ''} Consignment Packet`,
    message: CONSIGN_MESSAGE,
  });

  const startSigning = async () => {
    if (!validateForm(car)) return;
    if (!buildSigners().length) return StateManager.setAlertAndOpen('Add a consignor with an email address first.', 'error');
    StateManager.setLoading(true);
    const response = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(false) });
    StateManager.setLoading(false);
    if (response.status === 'complete') StateManager.setAlertAndOpen('Agreement sent for signature!', 'success');
    else StateManager.setAlertAndOpen(response.message || 'Hmm… there was an error.', 'error');
  };

  const signInPerson = async () => {
    if (!validateForm(car)) return;
    const list = buildSigners();
    if (!list.length) return StateManager.setAlertAndOpen('Add a consignor first.', 'error');
    StateManager.setLoading(true);
    const response = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(true) });
    StateManager.setLoading(false);
    if (response.status === 'complete') {
      setInPersonUrl(response.signUrl);
      setInPersonSigner(list[0].name);
      setInPersonOpen(true);
    } else StateManager.setAlertAndOpen(response.message || 'Could not start in-person signing.', 'error');
  };

  const printChecks = () => {
    setPrint(true);
    writeCheck(car).finally(() => setPrint(false));
  };

  // Permissions
  // The consignment rep owns the NTO conversation with the consignor, so the rep can
  // move the amount — but only with proof of what was agreed, which UpdateNTODialog
  // requires of everyone except admins. A rep qualifies either by holding `update-nto`
  // or by being the rep assigned to this car. Note this is UI-level gating: cars stay
  // on the cheap signedIn() Firestore rule, so it deters mistakes rather than enforcing
  // a hard boundary.
  // consign_rep is stored as a display name ("First Last"), the same shape as
  // StateManager.userName, so attribution matches on name the way rep pay already does.
  const sameName = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  const isOwnConsignment = sameName(car.consign_rep, StateManager.userName);
  const hasDeal = ['success', 'sold'].includes(car.status);
  // A car arriving on consignment has no NTO and usually no consignment rep on it yet,
  // so `isOwnConsignment` cannot gate the FIRST NTO — the rep who negotiated the amount
  // has to get it onto the car before the consignment agreement can be generated and
  // signed. So the initial NTO is open to the sales side, the same roles that are
  // allowed to hold `update-nto` at all. Every later change still needs the permission,
  // the assigned rep, or an admin, and still needs proof.
  const needsFirstNto = !Number(car.nto) && !hasDeal;
  const isSalesSide = ['admin', 'manager', 'sales'].includes(userType);
  const canEditNto = isAdmin() || isAllowed('update-nto') || isOwnConsignment
    || (needsFirstNto && isSalesSide);
  // Which amount the dialog edits follows the deal, so the button says the same thing
  // the dialog will do. Keep this in step with UpdateNTODialog. Admins pick the amount
  // inside the dialog, so their button stays generic.
  const editableNtoLabel = isAdmin() ? 'NTO' : (hasDeal ? 'Updated NTO' : 'NTO');
  const showNto = isManager() || canEditNto || car.status !== 'sold';
  // Anyone who can edit the Updated NTO has to be able to see it.
  const showUpdatedNto = isAllowed('edit-updated-nto') || isAdmin() || canEditNto;

  /**
   * Reassign the sales credit on one past NTO change.
   *
   * The history is a plain array, so this is a read-modify-write like every other
   * writer of the field. New entries are only ever appended, so an existing entry keeps
   * its index — but the document is re-read and the entry re-checked before writing, so
   * a change saved from another tab can never be overwritten by a stale copy.
   */
  const saveEntryCredits = async (index, entry, credits) => {
    setSavingCredit(true);
    try {
      const ref = firebase.firestore().doc(`cars/${stockNumber}`);
      const snap = await ref.get();
      const list = (snap.exists && Array.isArray(snap.data().nto_history)) ? snap.data().nto_history : [];
      const current = list[index];
      const sameEntry = current
        && current.date === entry.date
        && current.field === entry.field
        && Number(current.amount) === Number(entry.amount);

      if (!sameEntry) {
        StateManager.setAlertAndOpen('That history entry has changed — reopen the car and try again', 'warning');
        return;
      }

      const next = list.map((item, i) => (i === index
        ? { ...item, credits: normalizeCredits(credits), credits_source: CREDIT_SOURCE.MANUAL }
        : item));

      await ref.set({ nto_history: next }, { merge: true });
      StateManager.updateCar({ nto_history: next });
      setEditingCredit(null);
      StateManager.setAlertAndOpen('Sales credit updated', 'success');
    } catch (err) {
      console.error('Error updating NTO sales credit:', err);
      StateManager.setAlertAndOpen('Could not update the sales credit: ' + (err.message || err), 'error');
    } finally {
      setSavingCredit(false);
    }
  };

  const openProof = async (fileId) => {
    try {
      const url = await store.getFileDownloadURL(fileId);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.click();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Could not open the proof file', 'error');
    }
  };

  // Completion chips
  const chips = [
    { label: 'Consignor', done: !!(consignor.display_name && consignor.license) },
    { label: 'Source', done: isValidCarSource(car.car_source) },
    { label: 'Contract', done: !!(car.contract_start && car.contract_end && car.consign_rep) },
    // The agreement is generated from the NTO, so a missing NTO blocks signing. Show it
    // next to the other prerequisites and open the dialog straight from the chip.
    ...(showNto ? [{ label: 'NTO', done: !!Number(car.nto), onClick: canEditNto ? () => setNtoDialogOpen(true) : undefined }] : []),
    { label: 'Title', done: !!(car.title_front && car.title_back) },
    { label: 'Signed', done: !!car.consignment_doc, onClick: () => setSigOpen(true) },
  ];

  const contractStartValue = car.contract_start
    ? moment(car.contract_start, ['MM/DD/YYYY', 'YYYY/MM/DD', 'YYYY-MM-DD', 'MM-DD-YYYY'])
    : null;
  const contractEndValue = car.contract_end
    ? moment(car.contract_end, ['MM/DD/YYYY', 'YYYY/MM/DD', 'YYYY-MM-DD', 'MM-DD-YYYY'])
    : null;

  return (
    <Box className="no-print" sx={{ mx: -2, mt: -2 }}>

      {/* Sticky action bar */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider',
        px: { xs: 2, sm: 3 }, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {chips.map(({ label, done, onClick }) => (
            <Chip
              key={label}
              label={label}
              size="small"
              icon={done
                ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />
              }
              color={done ? 'success' : 'default'}
              variant={done ? 'filled' : 'outlined'}
              onClick={onClick}
              clickable={!!onClick}
              data-sold-ok={label === 'Signed' || undefined}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={viewPDF} data-sold-ok>
            View Contract
          </Button>
          <Button variant="outlined" size="small" onClick={signInPerson}>
            Sign in Person
          </Button>
          <Button variant="contained" size="small" onClick={startSigning}>
            Send for Signature
          </Button>
          {isAllowed('checks') && (
            <Button variant="contained" color="success" size="small" disabled={printDisabled} onClick={printChecks}>
              Make NTO Check
            </Button>
          )}
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Grid container spacing={2}>

          {/* Left column */}
          <Grid item xs={12} md={6}>

            {/* Consignor */}
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <SectionHeader>Consignor</SectionHeader>
              <ConsignorCard customer={consignor} stockNumber={stockNumber} />
              {consignor.id && <CarSourceField car={car} />}
              {consignor.id && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <DocumentSlot
                    label="Driver's License"
                    hint="Front of consignor's driver's license"
                    Icon={CreditCardIcon}
                    fieldId="license"
                    storageFolder="customer_data"
                    storageFileName={`${consignor.id}-license`}
                    firestorePath={`customers/${consignor.id}`}
                    currentValue={consignor.license}
                    onUpdate={onLicenseUpdate}
                    size="md"
                  />
                </Box>
              )}
            </Paper>

            {/* Contract */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <SectionHeader>Contract</SectionHeader>
              <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Contract Start"
                    value={contractStartValue}
                    onChange={(newValue) => updater('contract_start', newValue && newValue.isValid() ? newValue.format('MM/DD/YYYY') : '')}
                    slotProps={{
                      ...datePickerSlotProps,
                      textField: { size: 'small', fullWidth: true },
                    }}
                  />
                  <DatePicker
                    label="Contract End"
                    value={contractEndValue}
                    onChange={(newValue) => updater('contract_end', newValue && newValue.isValid() ? newValue.format('MM/DD/YYYY') : '')}
                    slotProps={{
                      ...datePickerSlotProps,
                      textField: { size: 'small', fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
                <FormControl fullWidth size="small">
                  <InputLabel>Consignment Rep</InputLabel>
                  <Select
                    value={car.consign_rep || ''}
                    label="Consignment Rep"
                    onChange={(e) => updater('consign_rep', e.target.value)}
                  >
                    {(salesTeam.length ? salesTeam.map(t => t.name) : constants.consignors).map(name => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={6}>

            {/* Net to Owner — read-only. Every change goes through UpdateNTODialog so
                it is recorded in nto_history and can be tied back to signed paperwork. */}
            {showNto && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <SectionHeader>Net to Owner</SectionHeader>
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Original NTO</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {car.nto ? `$${Number(car.nto).toLocaleString()}` : '—'}
                      </Typography>
                    </Box>
                    {showUpdatedNto && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Updated NTO</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {car.updated_nto ? `$${Number(car.updated_nto).toLocaleString()}` : '—'}
                        </Typography>
                      </Box>
                    )}
                    {ntoReduction > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Reduction</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }} color="success.dark">
                          ${ntoReduction.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {canEditNto ? (
                      <Button
                        size="small"
                        variant={needsFirstNto ? 'contained' : 'outlined'}
                        startIcon={needsFirstNto ? undefined : <LockOpenIcon />}
                        onClick={() => setNtoDialogOpen(true)}
                      >
                        {needsFirstNto ? 'Set NTO' : `Update ${editableNtoLabel}`}
                      </Button>
                    ) : (
                      <Chip
                        size="small"
                        icon={<LockIcon />}
                        label="Locked — consignment rep or admin"
                        variant="outlined"
                      />
                    )}
                    {ntoHistory.length > 0 && (
                      <Button size="small" onClick={() => setNtoHistoryOpen(o => !o)} data-sold-ok>
                        {ntoHistoryOpen ? 'Hide' : 'Show'} history ({ntoHistory.length})
                      </Button>
                    )}
                  </Box>

                  <Collapse in={ntoHistoryOpen} unmountOnExit>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                      {ntoHistoryRows.map(({ entry, index }) => (
                        <Box key={`${entry.date}-${index}`} sx={{ mb: 1.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {entry.field === 'updated_nto' ? 'Updated NTO' : 'Original NTO'}
                            {entry.override ? ' (admin override)' : ''}
                            {': '}
                            {entry.previous != null ? `$${Number(entry.previous).toLocaleString()} → ` : ''}
                            ${Number(entry.amount).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {moment(entry.date).format('MMM D, YYYY')}
                            {entry.byName ? ` · ${entry.byName}` : ''}
                            {entry.fileId ? ' · ' : ''}
                            {entry.fileId && (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => openProof(entry.fileId)}
                                data-sold-ok
                                sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                              >
                                {entry.fileName || 'view proof'}
                              </Link>
                            )}
                          </Typography>
                          {entry.reason && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                              {entry.reason}
                            </Typography>
                          )}
                          {/* Only a reduction pays anybody, so only a reduction carries a credit. */}
                          {entry.previous != null && Number(entry.amount) < Number(entry.previous) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {'Credit: '}
                              {creditsLabel(resolveNtoEntryCredits(entry, car)) || 'nobody'}
                              {!Array.isArray(entry.credits) && car.consign_rep ? ' (consignment rep)' : ''}
                              {entry.credits_locked
                                ? ' · paid'
                                : canEditNto && (
                                  <>
                                    {' · '}
                                    <Link
                                      component="button"
                                      type="button"
                                      underline="hover"
                                      onClick={() => setEditingCredit({ index, entry })}
                                      sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                                    >
                                      change
                                    </Link>
                                  </>
                                )}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              </Paper>
            )}

            {/* Title Documents */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <SectionHeader>Title Documents</SectionHeader>
              <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  <DocumentSlot
                    label="Title Front"
                    Icon={ArticleIcon}
                    fieldId="title_front"
                    storageFolder="titles"
                    storageFileName={`${stockNumber}-title_front`}
                    firestorePath={`cars/${stockNumber}`}
                    currentValue={car.title_front}
                    onUpdate={onCarFieldUpdate}
                    size="sm"
                  />
                  <DocumentSlot
                    label="Title Back"
                    Icon={ArticleIcon}
                    fieldId="title_back"
                    storageFolder="titles"
                    storageFileName={`${stockNumber}-title_back`}
                    firestorePath={`cars/${stockNumber}`}
                    currentValue={car.title_back}
                    onUpdate={onCarFieldUpdate}
                    size="sm"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!car.is_actual}
                        size="small"
                        onChange={(e) => updater('is_actual', e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Actual Miles</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!car.has_memorabilia}
                        size="small"
                        onChange={(e) => updater('has_memorabilia', e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Has Memorabilia</Typography>}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

{/* Agreement — full width */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <SectionHeader>Agreement</SectionHeader>
              <Box sx={{ p: 2 }}>
                <DocumentSlot
                  label="Signed Consignment Agreement"
                  hint="Upload the countersigned consignment packet"
                  Icon={GavelIcon}
                  fieldId="consignment_doc"
                  storageFolder="consignments"
                  storageFileName={`${stockNumber}-consignment_doc`}
                  firestorePath={`cars/${stockNumber}`}
                  currentValue={car.consignment_doc}
                  onUpdate={onCarFieldUpdate}
                  size="lg"
                />
              </Box>
            </Paper>
          </Grid>

          {/* Bill of Sale — full width. A signed copy from e-sign lands in consignor_bos_doc. */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <SectionHeader>Bill of Sale</SectionHeader>
              {/* data-sold-ok: the bill of sale usually goes out once the car has sold. */}
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }} data-sold-ok>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" size="small" onClick={() => setBosOpen(true)}>
                    Send Bill of Sale
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => setBosStatusOpen(true)}>
                    Signature Status
                  </Button>
                </Box>
                <DocumentSlot
                  label="Signed Consignor Bill of Sale"
                  hint="Filled in automatically once the consignor signs"
                  Icon={ReceiptLongIcon}
                  fieldId="consignor_bos_doc"
                  storageFolder="consignments"
                  storageFileName={`${stockNumber}-consignor_bos_doc`}
                  firestorePath={`cars/${stockNumber}`}
                  currentValue={car.consignor_bos_doc}
                  onUpdate={onCarFieldUpdate}
                  size="lg"
                />
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </Box>

      <SignatureStatusDialog
        open={sigOpen}
        onClose={() => setSigOpen(false)}
        car={car}
        type="consignment"
        label="Consignment Agreement"
      />

      <SignatureStatusDialog
        open={bosStatusOpen}
        onClose={() => setBosStatusOpen(false)}
        car={car}
        type="consignor_bos"
        label="Consignor Bill of Sale"
      />

      {bosOpen && <ConsignorBosDialog open={bosOpen} onClose={() => setBosOpen(false)} car={car} />}

      <InPersonSignDialog
        open={inPersonOpen}
        onClose={() => setInPersonOpen(false)}
        signUrl={inPersonUrl}
        signerName={inPersonSigner}
      />

      {ntoDialogOpen && (
        <UpdateNTODialog
          open={ntoDialogOpen}
          onClose={() => setNtoDialogOpen(false)}
          car={car}
        />
      )}

      {editingCredit && (
        <SalesCreditDialog
          open={true}
          onClose={() => setEditingCredit(null)}
          onSave={(credits) => saveEntryCredits(editingCredit.index, editingCredit.entry, credits)}
          value={resolveNtoEntryCredits(editingCredit.entry, car)}
          saving={savingCredit}
          title="Sales Credit — NTO Reduction"
          subtitle={`${stockNumber} · ${moment(editingCredit.entry.date).format('MMM D, YYYY')} · $${Number(editingCredit.entry.previous || 0).toLocaleString()} → $${Number(editingCredit.entry.amount).toLocaleString()}`}
          helperText="Who negotiated this reduction. The consignment rep is only the default — credit the rep who actually did the work, or split it."
        />
      )}
    </Box>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        px: 2, pt: 1.5, pb: 0.5,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
        color: 'text.secondary',
        borderBottom: 1, borderColor: 'divider',
      }}
    >
      {children}
    </Typography>
  );
}

function validateForm(data) {
  let message = false;
  if (!data.consignor) message = 'Missing Consignor Data';
  else if (!data.consignor?.email) message = 'Missing consignor email';
  else if (!data.consignor?.license) message = 'Missing consignor driver\'s license';
  else if (validateCarSource(data)) message = validateCarSource(data);
  else if (!data.nto) message = 'Missing NTO';
  else if (!data.contract_end) message = 'Missing Contract End';
  else if (!data.contract_start) message = 'Missing Contract Start';
  else if (!data.year) message = 'Missing Car Year';
  else if (!data.make) message = 'Missing Car Make';
  else if (!data.model) message = 'Missing Car Model';
  else if (!data.vin) message = 'Missing VIN';
  else if (!data.miles) message = 'Missing Miles';
  else if (!data.color) message = 'Missing Color';
  if (!message) return 'success';
  StateManager.setLoading(false);
  StateManager.setAlertAndOpen(message, 'error');
}

const writeCheck = async (car) => {
  StateManager.setAlertAndOpen('Making check…', 'info');

  const ntoSnap = await firebase.firestore().doc(`purchases/${car.stock}-NTO`).get();
  const amount = car.updated_nto || (ntoSnap.exists ? ntoSnap.data().amount : null) || car.nto;
  const payee = car.consignor.display_name;

  const response = await RequestManager.post({
    function: 'createNTOCheck',
    variables: { stock: car.stock, amount, payee, dealDate: car.deal?.date || '', year: car.year || '', make: car.make || '', model: car.model || '', vin: car.vin || '' },
  });

  if (response.success) {
    StateManager.setAlertAndOpen(`Check created!`, 'success');
  } else {
    StateManager.setAlertAndOpen(response.message || 'Check not created', 'error');
  }
};
